package org.example.project.client.api

import io.ktor.client.HttpClient
import io.ktor.client.plugins.HttpRequestTimeoutException
import io.ktor.client.request.header
import io.ktor.client.request.request
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpMethod
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import kotlinx.coroutines.CancellationException
import kotlinx.serialization.SerializationException
import kotlinx.serialization.json.Json
import org.example.project.client.config.ApiConfig
import org.example.project.client.error.ConflictException
import org.example.project.client.error.ForbiddenException
import org.example.project.client.error.MissingCsrfTokenException
import org.example.project.client.error.NetworkException
import org.example.project.client.error.RateLimitedException
import org.example.project.client.error.RequestTimeoutException
import org.example.project.client.error.ServerErrorException
import org.example.project.client.error.UnauthorizedException
import org.example.project.client.error.UnexpectedPayloadException
import org.example.project.client.error.ValidationException
import org.example.project.client.model.ApiErrorResponse
import org.example.project.client.model.CreateEventRequest
import org.example.project.client.model.EventResponse
import org.example.project.client.model.FavoritesResponse
import org.example.project.client.model.UpdateEventRequest
import org.example.project.client.session.SessionRepository
import org.example.project.client.session.SessionState

interface PlannerApiClient {
    suspend fun listEvents(
        bearerToken: String? = null,
        csrfToken: String? = null,
        isWeb: Boolean = false
    ): List<EventResponse>

    suspend fun getEvent(
        id: String,
        bearerToken: String? = null,
        csrfToken: String? = null,
        isWeb: Boolean = false
    ): EventResponse

    suspend fun createEvent(
        request: CreateEventRequest,
        bearerToken: String? = null,
        csrfToken: String? = null,
        isWeb: Boolean = false
    ): EventResponse

    suspend fun updateEvent(
        id: String,
        request: UpdateEventRequest,
        bearerToken: String? = null,
        csrfToken: String? = null,
        isWeb: Boolean = false
    ): EventResponse

    suspend fun deleteEvent(
        id: String,
        expectedVersion: Long,
        bearerToken: String? = null,
        csrfToken: String? = null,
        isWeb: Boolean = false
    )

    suspend fun getFavorites(
        bearerToken: String? = null,
        csrfToken: String? = null,
        isWeb: Boolean = false
    ): FavoritesResponse

    suspend fun addFavorite(
        activityId: String,
        bearerToken: String? = null,
        csrfToken: String? = null,
        isWeb: Boolean = false
    )

    suspend fun removeFavorite(
        activityId: String,
        bearerToken: String? = null,
        csrfToken: String? = null,
        isWeb: Boolean = false
    )

    fun close()
}

class DefaultPlannerApiClient(
    private val httpClient: HttpClient,
    private val apiConfig: ApiConfig,
    private val json: Json = Json {
        ignoreUnknownKeys = true
        isLenient = false
    },
    private val ownsHttpClient: Boolean = false
) : PlannerApiClient {

    override suspend fun listEvents(
        bearerToken: String?,
        csrfToken: String?,
        isWeb: Boolean
    ): List<EventResponse> {
        val url = apiConfig.resolve("/events")
        val headers = createHeaders(bearerToken, csrfToken, isWeb)
        val response = executeRequest(url, HttpMethod.Get, headers)
        return decodeBody(response)
    }

    override suspend fun getEvent(
        id: String,
        bearerToken: String?,
        csrfToken: String?,
        isWeb: Boolean
    ): EventResponse {
        val cleanId = id.trim()
        val url = apiConfig.resolve("/events/$cleanId")
        val headers = createHeaders(bearerToken, csrfToken, isWeb)
        val response = executeRequest(url, HttpMethod.Get, headers)
        return decodeBody(response)
    }

    override suspend fun createEvent(
        request: CreateEventRequest,
        bearerToken: String?,
        csrfToken: String?,
        isWeb: Boolean
    ): EventResponse {
        if (isWeb && csrfToken.isNullOrBlank()) {
            throw MissingCsrfTokenException("Missing CSRF token for web event creation")
        }
        val url = apiConfig.resolve("/events")
        val headers = createHeaders(bearerToken, csrfToken, isWeb)
        val response = executeRequest(url, HttpMethod.Post, headers, request)
        return decodeBody(response)
    }

    override suspend fun updateEvent(
        id: String,
        request: UpdateEventRequest,
        bearerToken: String?,
        csrfToken: String?,
        isWeb: Boolean
    ): EventResponse {
        if (isWeb && csrfToken.isNullOrBlank()) {
            throw MissingCsrfTokenException("Missing CSRF token for web event update")
        }
        val cleanId = id.trim()
        val url = apiConfig.resolve("/events/$cleanId")
        val headers = createHeaders(bearerToken, csrfToken, isWeb)
        val response = executeRequest(url, HttpMethod.Put, headers, request)
        return decodeBody(response)
    }

    override suspend fun deleteEvent(
        id: String,
        expectedVersion: Long,
        bearerToken: String?,
        csrfToken: String?,
        isWeb: Boolean
    ) {
        if (isWeb && csrfToken.isNullOrBlank()) {
            throw MissingCsrfTokenException("Missing CSRF token for web event deletion")
        }
        val cleanId = id.trim()
        val url = apiConfig.resolve("/events/$cleanId?expectedVersion=$expectedVersion")
        val headers = createHeaders(bearerToken, csrfToken, isWeb)
        executeRequest(url, HttpMethod.Delete, headers)
    }

    override suspend fun getFavorites(
        bearerToken: String?,
        csrfToken: String?,
        isWeb: Boolean
    ): FavoritesResponse {
        val url = apiConfig.resolve("/favorites")
        val headers = createHeaders(bearerToken, csrfToken, isWeb)
        val response = executeRequest(url, HttpMethod.Get, headers)
        return decodeBody(response)
    }

    override suspend fun addFavorite(
        activityId: String,
        bearerToken: String?,
        csrfToken: String?,
        isWeb: Boolean
    ) {
        if (isWeb && csrfToken.isNullOrBlank()) {
            throw MissingCsrfTokenException("Missing CSRF token for web add favorite")
        }
        val cleanId = activityId.trim()
        val url = apiConfig.resolve("/favorites/$cleanId")
        val headers = createHeaders(bearerToken, csrfToken, isWeb)
        executeRequest(url, HttpMethod.Post, headers)
    }

    override suspend fun removeFavorite(
        activityId: String,
        bearerToken: String?,
        csrfToken: String?,
        isWeb: Boolean
    ) {
        if (isWeb && csrfToken.isNullOrBlank()) {
            throw MissingCsrfTokenException("Missing CSRF token for web remove favorite")
        }
        val cleanId = activityId.trim()
        val url = apiConfig.resolve("/favorites/$cleanId")
        val headers = createHeaders(bearerToken, csrfToken, isWeb)
        executeRequest(url, HttpMethod.Delete, headers)
    }

    private fun createHeaders(
        bearerToken: String?,
        csrfToken: String?,
        isWeb: Boolean
    ): Map<String, String> {
        val headers = mutableMapOf<String, String>()
        if (!bearerToken.isNullOrBlank()) {
            headers[HttpHeaders.Authorization] = "Bearer $bearerToken"
        }
        if (isWeb && !csrfToken.isNullOrBlank()) {
            headers["X-CSRF-Token"] = csrfToken
        }
        return headers
    }

    private suspend fun executeRequest(
        url: String,
        method: HttpMethod,
        headers: Map<String, String> = emptyMap(),
        body: Any? = null
    ): HttpResponse {
        require(apiConfig.isSameOrigin(url)) {
            "Request URL violates origin boundary of configured API base URL"
        }

        val httpResponse: HttpResponse = try {
            httpClient.request(url) {
                this.method = method
                if (body != null) {
                    contentType(ContentType.Application.Json)
                    setBody(body)
                }
                headers.forEach { (name, value) ->
                    header(name, value)
                }
            }
        } catch (e: CancellationException) {
            throw e
        } catch (_: HttpRequestTimeoutException) {
            throw RequestTimeoutException()
        } catch (e: Exception) {
            val msg = e.message ?: ""
            if (msg.contains("timeout", ignoreCase = true)) {
                throw RequestTimeoutException()
            }
            throw NetworkException()
        }

        checkStatusAndThrow(httpResponse)
        return httpResponse
    }

    private suspend fun checkStatusAndThrow(response: HttpResponse) {
        val status = response.status
        if (status.value in 200..299) {
            return
        }

        val requestIdHeader = response.headers[HttpHeaders.XRequestId] ?: "unknown"
        val bodyText = try {
            response.bodyAsText()
        } catch (e: CancellationException) {
            throw e
        } catch (_: Exception) {
            ""
        }

        val errorResponse = try {
            if (bodyText.isNotBlank()) json.decodeFromString<ApiErrorResponse>(bodyText) else null
        } catch (_: Exception) {
            null
        }

        val errorCode = errorResponse?.error ?: when (status) {
            HttpStatusCode.BadRequest -> "BAD_REQUEST"
            HttpStatusCode.Unauthorized -> "UNAUTHORIZED"
            HttpStatusCode.Forbidden -> "FORBIDDEN"
            HttpStatusCode.NotFound -> "NOT_FOUND"
            HttpStatusCode.Conflict -> "CONFLICT"
            HttpStatusCode.TooManyRequests -> "TOO_MANY_REQUESTS"
            else -> "HTTP_${status.value}"
        }
        val errorMessage = errorResponse?.message ?: "HTTP ${status.value}"
        val reqId = errorResponse?.requestId ?: requestIdHeader

        when (status) {
            HttpStatusCode.BadRequest, HttpStatusCode.NotFound -> {
                throw ValidationException(status.value, errorCode, errorMessage, reqId)
            }
            HttpStatusCode.Unauthorized -> throw UnauthorizedException(status.value, errorCode, errorMessage, reqId)
            HttpStatusCode.Forbidden -> throw ForbiddenException(status.value, errorCode, errorMessage, reqId)
            HttpStatusCode.Conflict -> throw ConflictException(status.value, errorCode, errorMessage, reqId)
            HttpStatusCode.TooManyRequests -> {
                val retryAfter = response.headers[HttpHeaders.RetryAfter]?.toLongOrNull()
                throw RateLimitedException(status.value, errorCode, errorMessage, reqId, retryAfter)
            }
            else -> {
                if (status.value >= 500) {
                    throw ServerErrorException(status.value, errorCode, errorMessage, reqId)
                }
                throw ValidationException(status.value, errorCode, errorMessage, reqId)
            }
        }
    }

    private suspend inline fun <reified T> decodeBody(response: HttpResponse): T {
        val text = try {
            response.bodyAsText()
        } catch (e: CancellationException) {
            throw e
        } catch (_: Exception) {
            throw UnexpectedPayloadException()
        }

        return try {
            json.decodeFromString<T>(text)
        } catch (_: SerializationException) {
            throw UnexpectedPayloadException()
        }
    }

    override fun close() {
        if (ownsHttpClient) {
            httpClient.close()
        }
    }
}

// --- Session Integration Extensions ---

suspend fun PlannerApiClient.listEventsWithSession(sessionRepository: SessionRepository): List<EventResponse> {
    return when (val state = sessionRepository.sessionState.value) {
        is SessionState.AuthenticatedAndroid -> listEvents(bearerToken = state.token, isWeb = false)
        is SessionState.AuthenticatedWeb -> listEvents(csrfToken = state.csrfToken, isWeb = true)
        is SessionState.Unauthenticated -> throw UnauthorizedException(401, "UNAUTHORIZED", "Authentication required", "client")
    }
}

suspend fun PlannerApiClient.getEventWithSession(id: String, sessionRepository: SessionRepository): EventResponse {
    return when (val state = sessionRepository.sessionState.value) {
        is SessionState.AuthenticatedAndroid -> getEvent(id, bearerToken = state.token, isWeb = false)
        is SessionState.AuthenticatedWeb -> getEvent(id, csrfToken = state.csrfToken, isWeb = true)
        is SessionState.Unauthenticated -> throw UnauthorizedException(401, "UNAUTHORIZED", "Authentication required", "client")
    }
}

suspend fun PlannerApiClient.createEventWithSession(
    request: CreateEventRequest,
    sessionRepository: SessionRepository
): EventResponse {
    return when (val state = sessionRepository.sessionState.value) {
        is SessionState.AuthenticatedAndroid -> createEvent(request, bearerToken = state.token, isWeb = false)
        is SessionState.AuthenticatedWeb -> {
            if (state.reauthenticationRequiredForMutation || state.csrfToken == null) {
                throw MissingCsrfTokenException()
            }
            createEvent(request, csrfToken = state.csrfToken, isWeb = true)
        }
        is SessionState.Unauthenticated -> throw UnauthorizedException(401, "UNAUTHORIZED", "Authentication required", "client")
    }
}

suspend fun PlannerApiClient.updateEventWithSession(
    id: String,
    request: UpdateEventRequest,
    sessionRepository: SessionRepository
): EventResponse {
    return when (val state = sessionRepository.sessionState.value) {
        is SessionState.AuthenticatedAndroid -> updateEvent(id, request, bearerToken = state.token, isWeb = false)
        is SessionState.AuthenticatedWeb -> {
            if (state.reauthenticationRequiredForMutation || state.csrfToken == null) {
                throw MissingCsrfTokenException()
            }
            updateEvent(id, request, csrfToken = state.csrfToken, isWeb = true)
        }
        is SessionState.Unauthenticated -> throw UnauthorizedException(401, "UNAUTHORIZED", "Authentication required", "client")
    }
}

suspend fun PlannerApiClient.deleteEventWithSession(
    id: String,
    expectedVersion: Long,
    sessionRepository: SessionRepository
) {
    when (val state = sessionRepository.sessionState.value) {
        is SessionState.AuthenticatedAndroid -> deleteEvent(id, expectedVersion, bearerToken = state.token, isWeb = false)
        is SessionState.AuthenticatedWeb -> {
            if (state.reauthenticationRequiredForMutation || state.csrfToken == null) {
                throw MissingCsrfTokenException()
            }
            deleteEvent(id, expectedVersion, csrfToken = state.csrfToken, isWeb = true)
        }
        is SessionState.Unauthenticated -> throw UnauthorizedException(401, "UNAUTHORIZED", "Authentication required", "client")
    }
}

suspend fun PlannerApiClient.getFavoritesWithSession(sessionRepository: SessionRepository): FavoritesResponse {
    return when (val state = sessionRepository.sessionState.value) {
        is SessionState.AuthenticatedAndroid -> getFavorites(bearerToken = state.token, isWeb = false)
        is SessionState.AuthenticatedWeb -> getFavorites(csrfToken = state.csrfToken, isWeb = true)
        is SessionState.Unauthenticated -> throw UnauthorizedException(401, "UNAUTHORIZED", "Authentication required", "client")
    }
}

suspend fun PlannerApiClient.addFavoriteWithSession(activityId: String, sessionRepository: SessionRepository) {
    when (val state = sessionRepository.sessionState.value) {
        is SessionState.AuthenticatedAndroid -> addFavorite(activityId, bearerToken = state.token, isWeb = false)
        is SessionState.AuthenticatedWeb -> {
            if (state.reauthenticationRequiredForMutation || state.csrfToken == null) {
                throw MissingCsrfTokenException()
            }
            addFavorite(activityId, csrfToken = state.csrfToken, isWeb = true)
        }
        is SessionState.Unauthenticated -> throw UnauthorizedException(401, "UNAUTHORIZED", "Authentication required", "client")
    }
}

suspend fun PlannerApiClient.removeFavoriteWithSession(activityId: String, sessionRepository: SessionRepository) {
    when (val state = sessionRepository.sessionState.value) {
        is SessionState.AuthenticatedAndroid -> removeFavorite(activityId, bearerToken = state.token, isWeb = false)
        is SessionState.AuthenticatedWeb -> {
            if (state.reauthenticationRequiredForMutation || state.csrfToken == null) {
                throw MissingCsrfTokenException()
            }
            removeFavorite(activityId, csrfToken = state.csrfToken, isWeb = true)
        }
        is SessionState.Unauthenticated -> throw UnauthorizedException(401, "UNAUTHORIZED", "Authentication required", "client")
    }
}
